import type { Product, ProductListingStatus } from '@shopgnd/shared';

/** Placeholder category while a new category request is pending admin review. */
export const PENDING_CATEGORY_ID = 'cat-pending';

export function isScheduledForFuturePublish(
  product: Pick<Product, 'scheduledPublishAt'>,
): boolean {
  if (!product.scheduledPublishAt) return false;
  return new Date(product.scheduledPublishAt).getTime() > Date.now();
}

export function isPublishedProduct(
  product: Pick<Product, 'listingStatus' | 'scheduledPublishAt'>,
): boolean {
  const status = product.listingStatus ?? 'published';
  if (status !== 'published') return false;
  if (isScheduledForFuturePublish(product)) return false;
  return true;
}

export function isPurchasableProduct(
  product: Pick<Product, 'listingStatus' | 'scheduledPublishAt' | 'stock' | 'allowBackorders'>,
): boolean {
  if (!isPublishedProduct(product)) return false;
  if (product.stock > 0) return true;
  return product.allowBackorders === true;
}

export function listingStatusLabel(status: ProductListingStatus | undefined): string {
  switch (status ?? 'published') {
    case 'published':
      return 'Live';
    case 'pending_category':
      return 'Pending category';
    case 'pending_review':
      return 'Pending review';
    case 'draft':
      return 'Draft';
    case 'rejected':
      return 'Rejected';
    default:
      return 'Live';
  }
}

export function listingStatusBadgeLabel(status: ProductListingStatus | undefined): string {
  switch (status ?? 'published') {
    case 'published':
      return 'Published';
    case 'pending_category':
    case 'pending_review':
      return 'Pending';
    case 'draft':
      return 'Draft';
    case 'rejected':
      return 'Rejected';
    default:
      return 'Published';
  }
}

export type StockStatusFilter = 'in_stock' | 'low_stock' | 'out_of_stock';

export function productStockStatus(
  product: Pick<Product, 'stock' | 'lowStockThreshold'>,
): StockStatusFilter {
  const threshold = product.lowStockThreshold ?? 5;
  if (product.stock <= 0) return 'out_of_stock';
  if (product.stock <= threshold) return 'low_stock';
  return 'in_stock';
}

export function conditionLabel(condition: Product['condition']): string {
  switch (condition ?? 'NEW') {
    case 'NEW':
      return 'New';
    case 'USED':
      return 'Used';
    case 'REFURBISHED':
      return 'Refurbished';
    default:
      return 'New';
  }
}

export function shippingClassLabel(shippingClass: Product['shippingClass']): string {
  switch (shippingClass ?? 'STANDARD') {
    case 'STANDARD':
      return 'Standard';
    case 'OVERSIZED':
      return 'Oversized';
    case 'FRAGILE':
      return 'Fragile';
    default:
      return 'Standard';
  }
}
